import styled from "@emotion/styled"
import { TextField } from "@mui/material"
import { DonationMessages } from "app/[locale]/(layout)/donation-application-form/page"
import { Controller } from "react-hook-form"
import { NewText, useIsDesktop } from "ui"
import CustomHelperText from "./form/CustomHelperText"

const StyledContainer = styled.div`
  input {
    font-size: 16p !important;
  }
  textarea {
    font-size: 16px !important;
  }
  label {
    font-size: 16px;
  }
`
const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
  }
`
const TextAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  max-width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 90%;
  }
`
const InputLengthContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-top: 4px;
`

const StyledButton = styled.button`
  background-color: white;
  display: flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 200px;
  border: 2px solid #000;
  outline: none;
  margin-left: auto;
  min-width: 121px;
  margin-top: 24px;
  &:active {
    outline: none;
  }
  &:focus {
    outline: none;
  }
  &:disabled {
    border: 2px solid #6f6f6f;
    color: #6f6f6f;
  }
`

type Props = {
  control: any
  errors: any
  watch: any
  setAtiveAcordion: any
  trigger: any
  messages: DonationMessages
}

const StepB: React.FC<Props> = ({
  control,
  errors,
  watch,
  setAtiveAcordion,
  trigger,
  messages,
}) => {
  const handleContinue = (e: any) => {
    e.preventDefault()

    trigger([
      "PartBDescription",
      "PartBProjectGoals",
      "PartBTargetAudienceName",
      "PartBTargetAudienceSupportingPeopleAmount",
      "PartBArea",
      "PartBProjectSchedule",
      "PartBChangesToAchieve",
      "PartBProjectSuccessYourOpinion",
    ]).then((res: any) => {
      if (res) {
        setAtiveAcordion(5)
        document
          // eslint-disable-next-line quotes
          .querySelector('#panel1a-header[aria-expanded="true"]')
          ?.scrollIntoView({ behavior: "smooth" })
      } else {
        setTimeout(() => {
          const firstError = document.querySelector<HTMLElement>(
            "[aria-invalid='true']",
          )
          firstError?.focus()
          firstError?.scrollIntoView({ behavior: "smooth", block: "center" })
        }, 100)
      }
    })
  }

  const isDesktop = useIsDesktop()
  return (
    <StyledContainer>
      <div role="group" aria-labelledby="general-description-heading">
        <NewText
          id="general-description-heading"
          desktopFontSize={18}
          desktopFontWeight="400"
          desktopLineHeight="36px"
          mobileFontSize={16}
          mobileFontWeight="400"
          mobileLineHeight={"26px"}
          component="p"
          color={"black"}
          style={{ maxWidth: "560px", marginTop: "8px", marginBottom: "16px" }}
        >
          {messages.generalDes}
        </NewText>
        <TextAreaContainer>
          <Controller
            name="PartBDescription"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={messages.generalDe1}
                type="text"
                variant="filled"
                error={!!errors.PartBDescription}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartBDescription}
                    text={messages.generalDescError}
                  />
                }
                fullWidth
                multiline
                className={"multiline"}
                rows={6}
                inputProps={{
                  maxLength: 500,
                }}
                sx={{
                  "& .MuiFormHelperText-root": {
                    right: 0,
                    bottom: "-22px",
                  },
                  "& .MuiInputLabel-root": {
                    top: "-5px",
                  },
                }}
              />
            )}
          />
          <InputLengthContainer>
            <NewText
              desktopFontSize={14}
              desktopFontWeight="400"
              desktopLineHeight="20px"
              mobileFontSize={12}
              mobileFontWeight="400"
              mobileLineHeight={"17.28px"}
              component="p"
              color={"black"}
            >
              {watch("PartBDescription")?.length || 0}/500
            </NewText>
          </InputLengthContainer>
        </TextAreaContainer>
      </div>
      <div role="group" aria-labelledby="primary-objectives-heading">
        <NewText
          id="primary-objectives-heading"
          desktopFontSize={18}
          desktopFontWeight="400"
          desktopLineHeight="36px"
          mobileFontSize={16}
          mobileFontWeight="400"
          mobileLineHeight={"23px"}
          component="p"
          color={"black"}
          style={{ maxWidth: "560px", marginTop: "16px" }}
        >
          {messages.thePrimary}
        </NewText>
        <NewText
          desktopFontSize={14}
          desktopFontWeight="400"
          desktopLineHeight="20px"
          mobileFontSize={12}
          mobileFontWeight="400"
          mobileLineHeight={"17px"}
          component="p"
          color={"black"}
          style={{ maxWidth: "560px", marginBottom: "16px" }}
        >
          {messages.pleaseDesc}
        </NewText>
        <TextAreaContainer>
          <Controller
            name="PartBProjectGoals"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={messages.specifying}
                type="text"
                variant="filled"
                error={!!errors.PartBProjectGoals}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartBProjectGoals}
                    text={messages.primaryObjectivesError}
                  />
                }
                fullWidth
                multiline
                className={"multiline"}
                rows={6}
                inputProps={{
                  maxLength: 500,
                }}
                sx={{
                  "& .MuiFormHelperText-root": {
                    right: 0,
                    bottom: "-22px",
                  },
                  "& .MuiInputLabel-root": {
                    top: "-5px",
                  },
                }}
              />
            )}
          />
          <InputLengthContainer>
            <NewText
              desktopFontSize={14}
              desktopFontWeight="400"
              desktopLineHeight="20px"
              mobileFontSize={12}
              mobileFontWeight="400"
              mobileLineHeight={"17.28px"}
              component="p"
              color={"black"}
            >
              {watch("PartBProjectGoals")?.length || 0}/500
            </NewText>
          </InputLengthContainer>
        </TextAreaContainer>
      </div>
      <div role="group" aria-labelledby="target-audience-heading">
        <NewText
          id="target-audience-heading"
          desktopFontSize={18}
          desktopFontWeight="400"
          desktopLineHeight="36px"
          mobileFontSize={16}
          mobileFontWeight="400"
          mobileLineHeight={"23px"}
          component="p"
          color={"black"}
          style={{ maxWidth: "560px", marginTop: "16px", marginBottom: "16px" }}
        >
          {messages.theTargetA}
        </NewText>
        <StyledInputContainer>
          <Controller
            name="PartBTargetAudienceName"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={messages.theTarget1}
                type="text"
                variant="filled"
                fullWidth={isDesktop ? false : true}
                error={!!errors.PartBTargetAudienceName}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartBTargetAudienceName}
                    text={messages.theTarget2}
                  />
                }
                sx={{
                  marginBottom: "37px",
                }}
              />
            )}
          />
          <Controller
            name="PartBTargetAudienceSupportingPeopleAmount"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={messages.theNumberO}
                type="text"
                fullWidth={isDesktop ? false : true}
                variant="filled"
                error={!!errors.PartBTargetAudienceSupportingPeopleAmount}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartBTargetAudienceSupportingPeopleAmount}
                    text={messages.theNumber1}
                  />
                }
                sx={{
                  marginBottom: "37px",
                  marginLeft: isDesktop ? "24px" : 0,
                }}
              />
            )}
          />
        </StyledInputContainer>
      </div>
      <div role="group" aria-labelledby="geographic-area-heading">
        <NewText
          id="geographic-area-heading"
          desktopFontSize={18}
          desktopFontWeight="400"
          desktopLineHeight="36px"
          mobileFontSize={16}
          mobileFontWeight="400"
          mobileLineHeight={"26px"}
          component="p"
          color={"black"}
          style={{ maxWidth: "560px", marginTop: "16px", marginBottom: "16px" }}
        >
          {messages.theGeograp}
        </NewText>
        <Controller
          name="PartBArea"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={messages.area}
              type="text"
              variant="filled"
              error={!!errors.PartBArea}
              fullWidth={isDesktop ? false : true}
              helperText={
                <CustomHelperText
                  completed={messages.completed}
                  render={fieldState.isTouched}
                  error={!!errors.PartBArea}
                  text={messages.theAreaEnt}
                />
              }
              sx={{
                marginBottom: "37px",
              }}
            />
          )}
        />
      </div>
      <div role="group" aria-labelledby="project-schedule-heading">
        <NewText
          id="project-schedule-heading"
          desktopFontSize={18}
          desktopFontWeight="400"
          desktopLineHeight="36px"
          mobileFontSize={16}
          mobileFontWeight="400"
          mobileLineHeight={"23px"}
          component="p"
          color={"black"}
          style={{ maxWidth: "560px", marginTop: "16px", marginBottom: "16px" }}
        >
          {messages.theProject}
        </NewText>
        <Controller
          name="PartBProjectSchedule"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={messages.schedule}
              type="text"
              variant="filled"
              error={!!errors.PartBProjectSchedule}
              fullWidth={isDesktop ? false : true}
              helperText={
                <CustomHelperText
                  completed={messages.completed}
                  render={fieldState.isTouched}
                  error={!!errors.PartBProjectSchedule}
                  text={messages.theEntered}
                />
              }
              sx={{
                marginBottom: "37px",
              }}
            />
          )}
        />
      </div>
      <div role="group" aria-labelledby="results-heading">
        <NewText
          id="results-heading"
          desktopFontSize={18}
          desktopFontWeight="400"
          desktopLineHeight="36px"
          mobileFontSize={16}
          mobileFontWeight="400"
          mobileLineHeight={"23px"}
          component="p"
          color={"black"}
          style={{ maxWidth: "560px", marginTop: "16px", marginBottom: "16px" }}
        >
          {messages.theResults}
        </NewText>
        <TextAreaContainer>
          <Controller
            name="PartBChangesToAchieve"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={messages.theResult1}
                type="text"
                variant="filled"
                error={!!errors.PartBChangesToAchieve}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartBChangesToAchieve}
                    text={messages.theResultsError}
                  />
                }
                fullWidth
                multiline
                className={"multiline"}
                rows={6}
                inputProps={{
                  maxLength: 500,
                }}
                sx={{
                  "& .MuiFormHelperText-root": {
                    right: 0,
                    bottom: "-22px",
                  },
                  "& .MuiInputLabel-root": {
                    top: "-5px",
                  },
                }}
              />
            )}
          />
          <InputLengthContainer>
            <NewText
              desktopFontSize={14}
              desktopFontWeight="400"
              desktopLineHeight="20px"
              mobileFontSize={12}
              mobileFontWeight="400"
              mobileLineHeight={"17.28px"}
              component="p"
              color={"black"}
            >
              {watch("PartBChangesToAchieve")?.length || 0}/500
            </NewText>
          </InputLengthContainer>
        </TextAreaContainer>
      </div>
      <div role="group" aria-labelledby="opinion-heading">
        <NewText
          id="opinion-heading"
          desktopFontSize={18}
          desktopFontWeight="400"
          desktopLineHeight="36px"
          mobileFontSize={16}
          mobileFontWeight="400"
          mobileLineHeight={"23px"}
          component="p"
          color={"black"}
          style={{ maxWidth: "560px", marginTop: "16px", marginBottom: "16px" }}
        >
          {messages.inYourOpin}
        </NewText>
        <TextAreaContainer>
          <Controller
            name="PartBProjectSuccessYourOpinion"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={messages.detailOfWh}
                type="text"
                variant="filled"
                error={!!errors.PartBProjectSuccessYourOpinion}
                helperText={
                  <CustomHelperText
                    completed={messages.completed}
                    render={fieldState.isTouched}
                    error={!!errors.PartBProjectSuccessYourOpinion}
                    text={messages.inYourOpinionError}
                  />
                }
                fullWidth
                multiline
                className={"multiline"}
                rows={6}
                inputProps={{
                  maxLength: 500,
                }}
                sx={{
                  "& .MuiFormHelperText-root": {
                    right: 0,
                    bottom: "-22px",
                  },
                  "& .MuiInputLabel-root": {
                    top: "-5px",
                  },
                }}
              />
            )}
          />
          <InputLengthContainer>
            <NewText
              desktopFontSize={14}
              desktopFontWeight="400"
              desktopLineHeight="20px"
              mobileFontSize={12}
              mobileFontWeight="400"
              mobileLineHeight={"17.28px"}
              component="p"
              color={"black"}
            >
              {watch("PartBProjectSuccessYourOpinion")?.length || 0}/500
            </NewText>
          </InputLengthContainer>
        </TextAreaContainer>
      </div>
      <StyledButton onClick={handleContinue}>
        <NewText
          desktopFontSize={16}
          desktopFontWeight="600"
          desktopLineHeight="23px"
          mobileFontSize={14}
          mobileFontWeight="600"
          mobileLineHeight={"20px"}
          component="p"
          color={"black"}
          marginRight="4px"
          maxWidth={"80%"}
        >
          {messages.toStep3}
        </NewText>
      </StyledButton>
    </StyledContainer>
  )
}

export default StepB
